import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';

import { AddressLookup } from 'dashboard/components/ReportInput/AddressLookup';
import { AdverseMedia } from 'dashboard/components/ReportInput/AdverseMedia';
import { BusinessAdverseMedia } from 'dashboard/components/ReportInput/BusinessAdverseMedia';
import { BusinessAssociatedPersons } from 'dashboard/components/ReportInput/BusinessAssociatedPersons';
import BusinessClassification from 'dashboard/components/ReportInput/BusinessClassification';
import { BusinessIndustryClassification } from 'dashboard/components/ReportInput/BusinessIndustryClassification';
import { BusinessLookup } from 'dashboard/components/ReportInput/BusinessLookup';
import { BusinessNonprofit } from 'dashboard/components/ReportInput/BusinessNonprofit';
import BusinessOnlinePresence from 'dashboard/components/ReportInput/BusinessOnlinePresence';
import { BusinessSocialMedia } from 'dashboard/components/ReportInput/BusinessSocialMedia';
import { BusinessWatchlist } from 'dashboard/components/ReportInput/BusinessWatchlist';
import { BusinessWebsite } from 'dashboard/components/ReportInput/BusinessWebsite';
import ChainalysisAddressScreening from 'dashboard/components/ReportInput/ChainalysisAddressScreening';
import { CryptoAddressWatchlist } from 'dashboard/components/ReportInput/CryptoAddressWatchlist';
import { CustomList } from 'dashboard/components/ReportInput/CustomList';
import { EmailAddress } from 'dashboard/components/ReportInput/EmailAddress';
import EquifaxOneview from 'dashboard/components/ReportInput/EquifaxOneview';
import { FinraBrokerCheck } from 'dashboard/components/ReportInput/FinraBrokerCheck';
import KyckrBusinessLookup from 'dashboard/components/ReportInput/KyckrBusinessLookup';
import MiddeskBusinessLookup from 'dashboard/components/ReportInput/MiddeskBusinessLookup';
import MxAccount from 'dashboard/components/ReportInput/MxAccount';
import { PhoneNumber } from 'dashboard/components/ReportInput/PhoneNumber';
import { PoliticallyExposedPerson } from 'dashboard/components/ReportInput/PoliticallyExposedPerson';
import { ProfileNonAuthoritative } from 'dashboard/components/ReportInput/ProfileNonAuthoritative';
import ReportInputIntegration from 'dashboard/components/ReportInput/ReportInputIntegration';
import { Screening } from 'dashboard/components/ReportInput/Screening';
import { SecActionLookup } from 'dashboard/components/ReportInput/SecActionLookup';
import SentilinkApplicationRisk from 'dashboard/components/ReportInput/SentilinkApplicationRisk';
import SentilinkScores from 'dashboard/components/ReportInput/SentilinkScores';
import { SocialMedia } from 'dashboard/components/ReportInput/SocialMedia';
import Synthetic from 'dashboard/components/ReportInput/Synthetic';
import TrmWalletScreening from 'dashboard/components/ReportInput/TrmWalletScreening';
import { Watchlist } from 'dashboard/components/ReportInput/Watchlist';
import Banner from 'dashboard/components-lib/Banner';
import { Report } from 'dashboard/models/report';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface ReportDetailsProps {
  report: Report;
  isRedacted: boolean;
}

export const ReportInput: VFC<ReportDetailsProps> = ({
  report,
  isRedacted,
}: ReportDetailsProps) => {
  if (isRedacted) {
    return <Banner icon={<CircleAlertIcon />}>This report has been redacted</Banner>;
  }

  switch (report.type) {
    case ApiModel.ReportAddressLookup:
      return <AddressLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSnapshotAdverseMedia:
    case ApiModel.ReportAdverseMedia:
      return <AdverseMedia report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessAdverseMedia:
      return <BusinessAdverseMedia report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessAssociatedPersons:
      return <BusinessAssociatedPersons report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessClassification:
      return <BusinessClassification report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessIndustryClassification:
      return <BusinessIndustryClassification report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessLookup:
      return <BusinessLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSnapshotBusinessNonprofit:
    case ApiModel.ReportBusinessNonprofit:
      return <BusinessNonprofit report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessOnlinePresence:
      return <BusinessOnlinePresence report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessSocialMedia:
      return <BusinessSocialMedia report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessWatchlist:
      return <BusinessWatchlist report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessWebsite:
      return <BusinessWebsite report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportChainalysisAddressScreening:
      return <ChainalysisAddressScreening report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportCryptoAddressWatchlist:
      return <CryptoAddressWatchlist report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportCustomList:
      return <CustomList report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportEmailAddress:
      return <EmailAddress report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportEquifaxOneview:
      return <EquifaxOneview report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportFinraBrokerCheck:
      return <FinraBrokerCheck report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportKyckrBusinessLookup:
      return <KyckrBusinessLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportMiddeskBusinessLookup:
      return <MiddeskBusinessLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportMxAccount:
      return <MxAccount report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportPhoneNumber:
      return <PhoneNumber report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportPoliticallyExposedPerson:
      return <PoliticallyExposedPerson report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportProfileNonAuthoritative:
      return <ProfileNonAuthoritative report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportScreening:
      return <Screening report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSecActionLookup:
      return <SecActionLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSentilinkApplicationRisk:
      return <SentilinkApplicationRisk report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSentilinkScores:
      return <SentilinkScores report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSocialMedia:
      return <SocialMedia report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSynthetic:
      return <Synthetic report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportTrmWalletScreening:
      return <TrmWalletScreening report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSnapshotWatchlist:
    case ApiModel.ReportWatchlist:
      return <Watchlist report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportCoinbaseCheckCryptoRisk:
    case ApiModel.ReportClearbitBusinessLookup:
      return <ReportInputIntegration report={report} isRedacted={isRedacted} />;
    case ApiModel.Report:
    case ApiModel.ReportBusinessLiensLookup: // BusinessLiens report has a built-in input
    case ApiModel.ReportBusinessRegistrationsLookup: // BusinessRegistrations report has a built-in input
    case ApiModel.ReportProfile: // Profile report has a built-in input
      return null;
    default:
      assertNever(report);
  }
};
